import store from '@/store';
import { PANEL_IDS } from '@/constants/panelIds';
import { error as debugError } from '@/utils/debugLogger';
import {
  VIEWER_TYPES,
  navigateToViewerWithAction,
} from '@/utils/viewerNavigationUtils';
import {
  TAG_SPATIAL_PROVENANCE as PROVENANCE,
  TAG_SPATIAL_RESULT_KINDS as RESULT_KIND,
  TAG_SPATIAL_VIEWERS as VIEWER,
} from '@/features/Tags/utils/tagSpatialNavigation';
import { openTagInfoPanel } from '@/features/Tags/utils/tagInfoPanel';

const SCAN_FALLBACK_REASON = 'scan-unavailable-nearest-fallback';
const SUPERSEDED_REASON = 'superseded-intent';
const UNAVAILABLE_SCAN_CODES = new Set(['SCAN_UNAVAILABLE', 'IMAGE_NOT_FOUND']);

function once(callback) {
  let fired = false;
  return (...args) => {
    if (fired) return undefined;
    fired = true;
    return callback?.(...args);
  };
}

function sameId(left, right) {
  if (left === null || left === undefined || right === null || right === undefined) return false;
  return String(left).toLowerCase() === String(right).toLowerCase();
}

function isUnavailableScanError(err) {
  return UNAVAILABLE_SCAN_CODES.has(err?.code) || err?.status === 404;
}

function defaultDependencies() {
  const forgeService = store.state?.forgeService;
  const requireForgeService = () => {
    if (!forgeService) throw new Error('The inspection info panels need the viewer page');
    return forgeService;
  };
  return {
    navigateToViewerWithAction,
    claimPrimaryViewer(viewer) {
      const mainApp = store.state?.mainApp;
      if (typeof mainApp?.changeView === 'function') mainApp.changeView(viewer, 1);
      else mainApp?.openViewPageWithType?.(viewer);
    },
    hasAnyViewerOpen() {
      return !!(store.state?.viewer || store.state?.planViewer || forgeService?.panoramaViewer);
    },
    getOpenPanoramaScanId() {
      return store.state?.selectedImage?.id ?? null;
    },
    openTagAtScanPosition(tag, options) {
      return forgeService?.openTagAtScanPosition(tag, options);
    },
    openTagAtLinkedSource(tag, options) {
      return forgeService?.openTagAtLinkedSource(tag, options);
    },
    openTagIn3d(tag, options) {
      return forgeService?.openTagIn3d(tag, options);
    },
    /** The tag's own info panel slot (tagInfoPanel.js), so an inspection never lands in Tags. */
    openInfoPanel(tag) {
      return openTagInfoPanel(tag, {
        tag(regular) {
          store.commit?.('SET_EDITING_TAG', regular);
          return store.dispatch?.('GENERAL/OPEN_PANEL', PANEL_IDS.TAGS);
        },
        inspection(inspection) {
          return requireForgeService().showEditInspectionUI(inspection);
        },
        // The full AVA Inspect DTO first, as every card click does (AvaInspectRow, the 3D icon).
        async avaInspect(avaInspect) {
          const full = await store.dispatch('FETCH_AVA_INSPECT_TAG_BY_ID', avaInspect.id);
          return requireForgeService().showEditAvaInspectUI(full);
        },
      });
    },
    clearPendingViewerCallbacks(requestOwner) {
      forgeService?.clearTagSpatialNavigationCallback?.(requestOwner);
    },
  };
}

function isLatest(intentId, dependencies) {
  return !intentId || typeof dependencies.isLatestIntent !== 'function'
    || dependencies.isLatestIntent(intentId);
}

function staleResult() {
  return { status: 'stale', reason: SUPERSEDED_REASON };
}

function safeScalar(value) {
  return ['string', 'number', 'boolean'].includes(typeof value) ? value : null;
}

function logUnexpectedError(stage, err, { tag, requestedIntent, source, plan }) {
  debugError('Tag spatial navigation failed', {
    stage,
    tagId: safeScalar(tag?.id),
    intent: safeScalar(requestedIntent),
    source: safeScalar(source),
    plan: {
      kind: safeScalar(plan?.kind),
      viewer: safeScalar(plan?.viewer),
      facilityId: safeScalar(plan?.facilityId),
      provenance: safeScalar(plan?.provenance),
      reason: safeScalar(plan?.reason),
      degraded: safeScalar(plan?.degraded),
    },
    error: {
      name: safeScalar(err?.name),
      code: safeScalar(err?.code),
      status: safeScalar(err?.status),
    },
  });
}

function current3dFallback(tag, facilityId) {
  return (tag?.spatialTargets || []).find((target) => sameId(target?.facilityId, facilityId)
    && target.viewer === VIEWER.MODEL_3D
    && (target.provenance === PROVENANCE.MODEL_ELEMENTS
      || target.provenance === PROVENANCE.POINT_3D));
}

/**
 * The inverse of current3dFallback, for the tag that reaches 3D only through a supplemented
 * point target. It owns no model elements, so when the 3D viewer never becomes usable - an
 * empty or still-loading model makes navigateToViewerWithAction return null, which execute3d
 * reports as a failure - its placement scan is the one honest destination left. Falling back
 * there beats dropping the user on the info panel.
 *
 * Point-only is identified exactly as the card requests it (the explicit isolate intent
 * carrying isolate=false), never by provenance alone: an anomaly WITH linked elements also
 * resolves to point-3d, and it must keep failing loudly instead of silently opening 360.
 */
function pointOnly360Fallback(plan, tag, requestedIntent, isolate) {
  if (requestedIntent !== 'isolate-3d' || isolate !== false) return null;
  const scanTarget = (tag?.spatialTargets || []).find((target) => sameId(target?.facilityId, plan.facilityId)
    && target.viewer === VIEWER.PANORAMA_360
    && target.provenance === PROVENANCE.FACILITY_SCAN_POSITION
    && target.hasPoint === true
    && safeScalar(target.image360Id) !== null);
  if (!scanTarget) return null;
  return {
    ...plan,
    viewer: VIEWER.PANORAMA_360,
    provenance: PROVENANCE.FACILITY_SCAN_POSITION,
    image360Id: scanTarget.image360Id,
  };
}

function squaredDistance(left, right) {
  const leftPoint = left?.point;
  if (!leftPoint || !right) return Number.POSITIVE_INFINITY;
  const coordinates = ['x', 'y', 'z'];
  if (!coordinates.every((key) => Number.isFinite(leftPoint[key]) && Number.isFinite(right[key]))) {
    return Number.POSITIVE_INFINITY;
  }
  return coordinates.reduce((total, key) => total + ((leftPoint[key] - right[key]) ** 2), 0);
}

async function nearestSameFacilityFallback(plan, tag, allow3d, canExecute, dependencies) {
  const candidates = await dependencies.getPanoramaCandidates?.({ facilityId: plan.facilityId }) || [];
  if (!canExecute()) return staleResult();
  const nearest = candidates
    .filter((candidate) => sameId(candidate?.facilityId, plan.facilityId)
      && candidate.state === 'available')
    .map((candidate, index) => ({ candidate, index, distance: squaredDistance(candidate, tag?.point) }))
    .filter(({ distance }) => Number.isFinite(distance))
    .sort((left, right) => left.distance - right.distance || left.index - right.index)[0]?.candidate;

  if (nearest) {
    if (!canExecute()) return staleResult();
    await dependencies.openFallbackPanorama?.(nearest, {
      facilityId: plan.facilityId,
      degraded: true,
    });
    return {
      status: 'executed',
      reason: SCAN_FALLBACK_REASON,
      degraded: true,
      viewer: VIEWER.PANORAMA_360,
      facilityId: plan.facilityId,
      image360Id: nearest.id,
    };
  }

  if (allow3d && current3dFallback(tag, plan.facilityId)) {
    if (!canExecute()) return staleResult();
    await dependencies.openTagIn3d?.(tag, { isolate: false, degraded: true });
    return {
      status: 'executed',
      reason: SCAN_FALLBACK_REASON,
      degraded: true,
      viewer: VIEWER.MODEL_3D,
      facilityId: plan.facilityId,
      image360Id: null,
    };
  }

  throw new Error('No same-facility spatial fallback is available.');
}

async function execute360(plan, tag, source, intentId, requestOwner, dependencies) {
  if (source === 'deep-link' || !dependencies.hasAnyViewerOpen?.()) {
    dependencies.claimPrimaryViewer?.(VIEWER.PANORAMA_360);
  }

  const reuseOpenScan = sameId(dependencies.getOpenPanoramaScanId?.(), plan.image360Id);
  const onReady = once(dependencies.onPanoramaReady);
  const options = {
    facilityId: plan.facilityId,
    image360Id: plan.image360Id,
    allowNearestFallback: plan.provenance === PROVENANCE.LINKED_SOURCE,
    requestOwner,
  };
  if (dependencies.includeReuseOpenScan === true) options.reuseOpenScan = reuseOpenScan;
  if (typeof dependencies.onPanoramaReady === 'function') options.onReady = onReady;
  if (intentId) options.shouldExecute = () => isLatest(intentId, dependencies);

  const primitive = plan.provenance === PROVENANCE.FACILITY_SCAN_POSITION
    ? dependencies.openTagAtScanPosition
    : dependencies.openTagAtLinkedSource;
  if (typeof primitive !== 'function') throw new Error('The 360 navigation primitive is unavailable.');

  let primitiveResult;
  const runPrimitive = async () => {
    try {
      primitiveResult = await primitive(tag, options);
      return primitiveResult;
    } catch (err) {
      primitiveResult = { executorError: err };
      return primitiveResult;
    }
  };
  const navigationResult = typeof dependencies.navigateToViewerWithAction === 'function'
    ? await dependencies.navigateToViewerWithAction(VIEWER_TYPES.PANORAMA_360, runPrimitive)
    : await runPrimitive();
  const result = navigationResult ?? primitiveResult;
  if (result?.executorError) throw result.executorError;
  if (result === null || result === undefined) {
    throw new Error('The 360 viewer did not execute the requested action.');
  }
  return result;
}

async function execute3d(plan, tag, isolate, source, intentId, requestOwner, dependencies) {
  if (source === 'deep-link' || !dependencies.hasAnyViewerOpen?.()) {
    dependencies.claimPrimaryViewer?.(VIEWER.MODEL_3D);
  }

  let primitiveResult;
  const runPrimitive = async () => {
    primitiveResult = await dependencies.openTagIn3d?.(tag, {
      isolate,
      requestOwner,
      shouldExecute: () => isLatest(intentId, dependencies),
    });
    return primitiveResult;
  };
  const navigationResult = typeof dependencies.navigateToViewerWithAction === 'function'
    ? await dependencies.navigateToViewerWithAction(VIEWER_TYPES.MODEL_3D, runPrimitive)
    : await runPrimitive();
  const result = navigationResult ?? primitiveResult;
  if (result === null || result === undefined) {
    throw new Error('The 3D viewer did not execute the requested action.');
  }
  return result;
}

async function executeCrossFacility(plan, tag, dependencies) {
  await dependencies.loadDestinationFacility?.(plan.facilityId);
  const destinationTag = await dependencies.hydrateTag?.({
    id: tag.id,
    assetId: plan.facilityId,
    expectedKind: null,
  });
  if (!destinationTag || typeof dependencies.executeDestinationPlan !== 'function') {
    return { status: 'navigated', reason: plan.reason };
  }
  return dependencies.executeDestinationPlan(destinationTag, {
    currentFacilityId: plan.facilityId,
    intent: 'auto-zoom',
    selectedFacilityId: null,
  });
}

/**
 * Executes one previously resolved tag navigation plan without recomputing it.
 */
export async function executeTagSpatialIntent({
  plan,
  tag,
  isolate = false,
  source = 'ui',
  requestedIntent = 'auto-zoom',
  intentId = null,
  dependencies: suppliedDependencies = {},
} = {}) {
  const dependencies = { ...defaultDependencies(), ...suppliedDependencies };
  const requestOwner = {};
  const failureContext = { tag, requestedIntent, source, plan };
  let panelAttempted = false;
  const canExecute = () => isLatest(intentId, dependencies);
  const openInfoPanelOnce = async () => {
    if (panelAttempted || !canExecute()) return false;
    panelAttempted = true;
    try {
      await dependencies.openInfoPanel?.(tag);
      return true;
    } catch (err) {
      logUnexpectedError('panel', err, failureContext);
      return false;
    }
  };
  dependencies.includeReuseOpenScan = Object.prototype.hasOwnProperty.call(
    suppliedDependencies,
    'getOpenPanoramaScanId',
  );
  if (!canExecute()) return staleResult();

  dependencies.setLoading?.(true, requestOwner);
  try {
    let result;
    if (plan?.kind === RESULT_KIND.CHOOSE_FACILITY) {
      result = { status: 'choose-facility', reason: plan.reason };
    } else if (plan?.kind === RESULT_KIND.PANEL) {
      const opened = await openInfoPanelOnce();
      result = { status: opened ? 'panel-opened' : 'failed', reason: plan.reason };
    } else if (plan?.kind === RESULT_KIND.UNAVAILABLE) {
      result = { status: 'unavailable', reason: plan.reason };
    } else if (plan?.kind === RESULT_KIND.NAVIGATE_FACILITY) {
      result = await executeCrossFacility(plan, tag, dependencies);
    } else if (plan?.kind === RESULT_KIND.VIEWER && plan.viewer === VIEWER.PANORAMA_360) {
      try {
        result = await execute360(plan, tag, source, intentId, requestOwner, dependencies);
      } catch (err) {
        if (!canExecute()) return staleResult();
        if (plan.provenance !== PROVENANCE.LINKED_SOURCE || !isUnavailableScanError(err)) {
          logUnexpectedError('360-navigation', err, failureContext);
          await openInfoPanelOnce();
          result = { status: 'failed', reason: plan.reason };
        } else {
          try {
            result = await nearestSameFacilityFallback(
              plan,
              tag,
              requestedIntent !== 'explicit-360',
              canExecute,
              dependencies,
            );
          } catch (fallbackErr) {
            if (!canExecute()) return staleResult();
            logUnexpectedError('fallback', fallbackErr, failureContext);
            await openInfoPanelOnce();
            result = { status: 'failed', reason: SCAN_FALLBACK_REASON };
          }
        }
      }
    } else if (plan?.kind === RESULT_KIND.VIEWER && plan.viewer === VIEWER.MODEL_3D) {
      try {
        result = await execute3d(plan, tag, isolate, source, intentId, requestOwner, dependencies);
      } catch (err) {
        if (!canExecute()) return staleResult();
        const scanPlan = pointOnly360Fallback(plan, tag, requestedIntent, isolate);
        if (!scanPlan) throw err;
        logUnexpectedError('3d-navigation', err, failureContext);
        result = await execute360(scanPlan, tag, source, intentId, requestOwner, dependencies);
      }
    } else {
      result = { status: 'unavailable', reason: plan?.reason || 'no-spatial-target' };
    }

    if (!canExecute()) return staleResult();
    return result;
  } catch (err) {
    if (!canExecute()) return staleResult();
    logUnexpectedError('execution', err, failureContext);
    await openInfoPanelOnce();
    return { status: 'failed', reason: plan?.reason || 'no-spatial-target' };
  } finally {
    dependencies.clearPendingViewerCallbacks?.(requestOwner);
    dependencies.setLoading?.(false, requestOwner);
  }
}
